import MainLayout from "@layouts/MainLayout";
import AdminPage from "@pages/admin";
import AdminContractsPage from "@pages/admin/contracts";
import AdminGroupsPage from "@pages/admin/groups";
import AdminGroupDetailPage from "@pages/admin/groups/group-detail";
import AdminIntegrationsPage from "@pages/admin/integrations";
import AdminMailLogPage from "@pages/admin/mail-log";
import AdminTemplatesPage from "@pages/admin/templates";
import AdminUserDetailPage from "@pages/admin/user-detail";
import LoginPage from "@pages/auth/login";
import ConnectionsPage from "@pages/communications/connections";
import CommunicationDriftPage from "@pages/communications/drift";
import CommunicationErrorsPage from "@pages/communications/errors";
import CommunicationEventsPage from "@pages/communications/events";
import CommunicationOutagesPage from "@pages/communications/outages";
import PollingLogPage from "@pages/communications/polling-log";
import CommunicationStatePage from "@pages/communications/state";
import DashboardPage from "@pages/dashboard";
import DevicesPage from "@pages/devices";
import ObjectsPage from "@pages/objects";
import AIAnalysisPage from "@pages/reports/ai-analysis";
import ReportsAutoreadPage from "@pages/reports/autoread";
import ReportsEventsPage from "@pages/reports/events";
import ReportsLastDataPage from "@pages/reports/last-data";
import ReportsMatrixPage from "@pages/reports/matrix";
import ReportsNetworkPage from "@pages/reports/network";
import ReportsNetworkTelemetryPage from "@pages/reports/networktelemetry";
import ReportsPhaseDiagramPage from "@pages/reports/phase-diagram";
import ReportsPowerQualityPage from "@pages/reports/power-quality";
import ReportsProfileDataPage from "@pages/reports/profile-data";
import ReportsReadingsPage from "@pages/reports/readings";
import RegulatoryReportsPage from "@pages/reports/regulatory";
import ReportsTouPage from "@pages/reports/tou";
import SettingsProfilePage from "@pages/settings/profile";
import SupportPage from "@pages/support";
import TelemetryPage from "@pages/telemetry";
import { authRefresh } from "@shared/api/generated/auth/auth";
import { isAdminUser } from "@shared/rbac";
import { useUiStore } from "@shared/store/uiStore";
import { useStoredUser } from "@shared/useStoredUser";
import { App as AntdApp, ConfigProvider, Flex, Spin, theme } from "antd";
import ruRU from "antd/locale/ru_RU";
import type { ReactElement } from "react";
import { useEffect, useMemo, useState } from "react";
import { Navigate, Outlet, useRoutes } from "react-router";

const getSystemTheme = (): "light" | "dark" => {
  if (typeof window === "undefined" || !window.matchMedia) {
    return "light";
  }
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
};

const RequireAuth = ({ children }: { children: ReactElement }) => {
  const [state, setState] = useState<"checking" | "ok" | "redirect">("checking");

  useEffect(() => {
    let cancelled = false;
    const access = typeof window !== "undefined" ? localStorage.getItem("acweb_access") : null;
    const refresh = typeof window !== "undefined" ? localStorage.getItem("acweb_refresh") : null;
    if (access) {
      setState("ok");
      return;
    }
    if (!refresh) {
      setState("redirect");
      return;
    }
    const refreshToken = async () => {
      try {
        const data = await authRefresh({ refresh });
        const token = data?.tokens?.access_token as string | undefined;
        const newRefresh = data?.tokens?.refresh_token as string | undefined;
        if (token) {
          localStorage.setItem("acweb_access", token);
        }
        if (newRefresh) {
          localStorage.setItem("acweb_refresh", newRefresh);
        }
        if (!cancelled) {
          setState(token ? "ok" : "redirect");
        }
      } catch {
        if (!cancelled) {
          setState("redirect");
        }
      }
    };
    refreshToken();
    return () => {
      cancelled = true;
    };
  }, []);

  if (state === "redirect") {
    return <Navigate to="/auth/login" replace />;
  }
  if (state === "checking") {
    return (
      <Flex align="center" justify="center" style={{ minHeight: "100vh" }}>
        <Spin size="large" />
      </Flex>
    );
  }
  return children;
};

const RequireAdmin = ({ children }: { children: ReactElement }) => {
  const user = useStoredUser();
  if (!isAdminUser(user)) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};

const App = () => {
  const mode = useUiStore((state) => state.themeMode);
  const setMode = useUiStore((state) => state.setThemeMode);

  const systemTheme = useMemo(getSystemTheme, []);
  const resolvedMode = mode === "system" ? systemTheme : mode;

  const routes = [
    { path: "/auth/login", element: <LoginPage /> },
    {
      path: "/",
      element: (
        <RequireAuth>
          <MainLayout mode={mode} onModeChange={setMode} />
        </RequireAuth>
      ),
      children: [
        { index: true, element: <Navigate to="/dashboard" replace /> },
        { path: "dashboard", element: <DashboardPage /> },
        {
          path: "assets",
          element: <Outlet />,
          children: [
            { index: true, element: <ObjectsPage /> },
            { path: "objects", element: <ObjectsPage /> },
            { path: "devices", element: <DevicesPage /> },
            {
              path: "connections",
              element: <ConnectionsPage />,
            },
          ],
        },
        {
          path: "monitoring",
          element: <Outlet />,
          children: [
            { index: true, element: <TelemetryPage /> },
            { path: "charts", element: <TelemetryPage /> },
            { path: "last-data", element: <ReportsLastDataPage /> },
            { path: "network", element: <ReportsNetworkPage /> },
            { path: "network-telemetry", element: <ReportsNetworkTelemetryPage /> },
            { path: "vectors", element: <ReportsPhaseDiagramPage /> },
            { path: "events", element: <ReportsEventsPage /> },
          ],
        },
        {
          path: "analytics",
          element: <Outlet />,
          children: [
            { index: true, element: <ReportsReadingsPage /> },
            { path: "readings", element: <ReportsReadingsPage /> },
            { path: "tou", element: <ReportsTouPage /> },
            { path: "autoread", element: <ReportsAutoreadPage /> },
            { path: "profile", element: <ReportsProfileDataPage /> },
            { path: "power-quality", element: <ReportsPowerQualityPage /> },
            { path: "matrix", element: <ReportsMatrixPage /> },
            { path: "regulatory", element: <RegulatoryReportsPage /> },
            { path: "ai-analysis", element: <AIAnalysisPage /> },
          ],
        },
        {
          path: "comms",
          element: <Outlet />,
          children: [
            { index: true, element: <CommunicationStatePage /> },
            { path: "state", element: <CommunicationStatePage /> },
            { path: "errors", element: <CommunicationErrorsPage /> },
            { path: "outages", element: <CommunicationOutagesPage /> },
            { path: "drift", element: <CommunicationDriftPage /> },
            { path: "events", element: <CommunicationEventsPage /> },
            { path: "polling-log", element: <PollingLogPage /> },
          ],
        },
        {
          path: "settings",
          element: <Outlet />,
          children: [
            { index: true, element: <SettingsProfilePage /> },
            { path: "profile", element: <SettingsProfilePage /> },
          ],
        },
        {
          path: "support",
          element: <Outlet />,
          children: [
            { index: true, element: <SupportPage /> },
            { path: "tickets", element: <SupportPage /> },
          ],
        },
        {
          path: "admin",
          element: (
            <RequireAdmin>
              <Outlet />
            </RequireAdmin>
          ),
          children: [
            { index: true, element: <AdminPage /> },
            { path: "users", element: <AdminPage /> },
            { path: "users/:id", element: <AdminUserDetailPage /> },
            { path: "groups", element: <AdminGroupsPage /> },
            { path: "groups/:id", element: <AdminGroupDetailPage /> },
            { path: "contracts", element: <AdminContractsPage /> },
            { path: "integrations", element: <AdminIntegrationsPage /> },
            { path: "integrations/:slug", element: <Navigate to="/admin/integrations" replace /> },
            { path: "mail", element: <AdminMailLogPage /> },
            { path: "templates", element: <AdminTemplatesPage /> },
          ],
        },
        { path: "*", element: <Navigate to="/dashboard" replace /> },
      ],
    },
  ];

  const element = useRoutes(routes);

  return (
    <ConfigProvider
      locale={ruRU}
      theme={{
        cssVar: { key: "acweb" },
        hashed: false,
        algorithm: resolvedMode === "dark" ? theme.darkAlgorithm : theme.defaultAlgorithm,
        token: {
          colorPrimary: "#1677ff",
          borderRadius: 8,
          controlHeight: 32,
          ...(resolvedMode === "light" && { colorBgLayout: "#f5f7fa" }),
          fontFamily:
            '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
        },
        components: {
          Layout: {
            headerHeight: 48,
            headerPadding: "0 16px",
          },
        },
      }}
    >
      <AntdApp>{element}</AntdApp>
    </ConfigProvider>
  );
};

export default App;
